import React, { useMemo, useState, useEffect } from 'react';
import { Icon, ICONS } from 'components/Icons';
import RectangularButton from 'components/elements/molecules/RectangularButton/RectangularButton';
import ActionLink from 'components/elements/molecules/ActionLink/ActionLink';

const EVENT_LABELS = {
  received_file: 'File received',
  uploading_to_storage: 'Uploading file to storage',
  uploaded_to_storage: 'File uploaded to storage',
  uploading_to_openai: 'Uploading file for analysis',
  uploaded_to_openai: 'File uploaded for analysis',
  analyzing_file: 'Analyzing syllabus file',
  analysis_completed: 'File analysis completed',
  saving_results: 'Saving syllabus rows',
};

const STATUS_DOTS = ['sending', 'analyzing', 'success'];

const getEtaLabel = (seconds, fallback) => {
  const value = Number(seconds);

  if (!Number.isFinite(value) || value <= 0) {
    return fallback;
  }

  const rounded = Math.max(1, Math.round(value));
  if (rounded >= 60) {
    const minutes = Math.round(rounded / 60);
    return `Estimated time: ${minutes} minute${minutes === 1 ? '' : 's'}`;
  }

  return `Estimated time: ${rounded} second${rounded === 1 ? '' : 's'}`;
};

export default function SyllabusUploadProgress({
  eventName,
  currentStep,
  estimatedSecondsRemaining,
  uploadUiState,
  uploadError,
  onSkip,
}) {
  const config = useMemo(() => {
    const eventDetail = EVENT_LABELS[eventName] || currentStep || '';

    if (uploadUiState === 'success') {
      return {
        title: 'We have found your course information',
        subtitle:
          'We found a course description, lecture dates, and lecture topics.',
        icon: ICONS.CHECKMARK_CIRCLE,
        iconClassName: 'success',
        iconSvgClassName: '',
        activeDot: 'success',
        rows: ['Course description', 'Lecture dates', 'Lecture topics'].map(
          (label) => ({
            label,
            icon: ICONS.CHECKMARK_CIRCLE,
            state: 'success',
          }),
        ),
      };
    }

    if (uploadUiState === 'retrying') {
      return {
        title: 'We are analyzing your file again',
        subtitle:
          getEtaLabel(estimatedSecondsRemaining, 'Estimated time: 5 seconds') ||
          '',
        icon: ICONS.TIME,
        iconClassName: 'retrying',
        iconSvgClassName: '',
        activeDot: 'analyzing',
        rows: [
          'Course title',
          'Course description',
          'Lecture topics and dates',
        ].map((label) => ({
          label,
          icon: ICONS.TIME,
          state: 'analyzing',
        })),
      };
    }

    if (uploadUiState === 'analyzing') {
      return {
        title: 'Analyzing your syllabus takes about 90 seconds',
        subtitle:
          'While we find your course topics and lecture dates, let’s configure how students will read.',
        icon: ICONS.TIME,
        iconClassName: 'analyzing',
        iconSvgClassName: '',
        activeDot: 'analyzing',
        rows: [
          // 'Course title',
          // 'Course description',
          // 'Lecture topics and dates',
        ].map((label) => ({
          label,
          icon: ICONS.TIME,
          state: 'analyzing',
        })),
      };
    }

    if (uploadUiState === 'error') {
      return {
        title: "We couldn't find your course information",
        subtitle:
          uploadError ||
          "We couldn't find a course description, lecture dates, or lecture topics.",
        icon: ICONS.DANGER_WARNING,
        iconClassName: 'error error-warning-triangle',
        iconSvgClassName: '',
        activeDot: 'analyzing',
        rows: [],
      };
    }

    return {
      title: 'Sending your file to Servers',
      subtitle:
        getEtaLabel(estimatedSecondsRemaining, 'Estimated time: 5 seconds') ||
        '',
      detail: eventDetail || '',
      icon: ICONS.TIME,
      iconClassName: 'sending sending-clock',
      iconSvgClassName: '',
      activeDot: 'sending',
      rows: [],
    };
  }, [
    currentStep,
    estimatedSecondsRemaining,
    eventName,
    uploadError,
    uploadUiState,
  ]);

  const [secondsElapsed, setSecondsElapsed] = useState(0);

  useEffect(() => {
    if (uploadUiState !== 'analyzing') {
      setSecondsElapsed(0);
      return;
    }

    setSecondsElapsed(0);
    const interval = setInterval(() => {
      setSecondsElapsed((prev) => {
        if (prev >= 90) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [uploadUiState]);

  const showIconAboveTitle =
    uploadUiState === 'sending' || uploadUiState === 'error';

  return (
    <div className="syllabus-upload-progress" role="status" aria-live="polite">
      {showIconAboveTitle ? (
        <>
          <div
            className={`syllabus-upload-progress-hero ${config.iconClassName}`}
          >
            <Icon icon={config.icon} className={config.iconSvgClassName} />
          </div>
          <div className="syllabus-upload-progress-title">{config.title}</div>
        </>
      ) : (
        <>
          <div className="syllabus-upload-progress-title">{config.title}</div>
        </>
      )}
      <div className="syllabus-upload-progress-subtitle">{config.subtitle}</div>
      {/* {uploadUiState === 'analyzing' && (
        <div className="mt-3">
          <div className="syllabus-upload-progress-timer">
            {secondsElapsed < 90
              ? `${secondsElapsed}s`
              : 'We need a little more time to process.'}
          </div>
        </div>
      )} */}
      {!!config.detail && (
        <div className="syllabus-upload-progress-detail">{config.detail}</div>
      )}
      {config.rows.length > 0 && (
        <div className="syllabus-upload-progress-list">
          {config.rows.map((row) => (
            <div
              className={`syllabus-upload-progress-list-row ${row.state}`}
              key={row.label}
            >
              <span className="syllabus-upload-progress-list-icon">
                <Icon icon={row.icon} />
              </span>
              <span>{row.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* {uploadUiState === 'analyzing' && (
        <div className="mt-3">
          <ActionLink
            label="Stop processing the file. I'll enter manually."
            linkClassName="av-blue mt-2"
            onClick={onSkip}
          />
        </div>
      )} */}

      {uploadUiState === 'error' && (
        <RectangularButton className="mt-3" onClick={onSkip}>
          Enter Manually
        </RectangularButton>
      )}
      <div className="syllabus-upload-progress-dots">
        {STATUS_DOTS.map((dot) => (
          <span
            key={dot}
            className={`status-dot ${dot === config.activeDot ? 'active' : ''}`}
          />
        ))}
      </div>
    </div>
  );
}
